/** Packages **/
import React, { Component } from 'react';
import Dimensions from 'react-dimensions';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import OpenTimeSearchItem from './OpenTimeSearchItem';
import CardHead from '../../components/CardHead';
import OpenTimeSearchTable from './OpenTimeSearchTable';
import { common, httpStatus, openTimeSearch, CATEGORY_ID, STATION_ID, calendar } from '../../data/constants';
import FilterBar from './components/FilterBar';
import * as actions from './../../data/openTimeSearch/actions';
import * as utils from '../../utilities/calendarUtilities';
import { between, isActivityOutOfPeriod } from './helpers.js';
import './styles.scss';
import { openTimeSearchConfig } from './../../settings/settings';
import * as OpenTimeSearchTypes from './OpenTimeSearchTypes';
import { useLanguage } from '../../context/LanguageContext';

class OpenTimeSearch extends Component {
  constructor(props) {
    super(props);

    const staffId = (this.props.user) ? this.props.user.staffId : '';
    let bidStart = this.props.currentBid.startDate ?
      this.props.currentBid.startDate : [1969, 12, 31];
    let bidEnd = this.props.currentBid.endDate ?
      this.props.currentBid.endDate : [1969, 12, 31];
    // Use Date.UTC to ensure consistent timestamps across all timezones
    bidStart = Date.UTC(bidStart[0], bidStart[1] - 1, bidStart[2]);
    bidEnd = new Date(Date.UTC(bidEnd[0], bidEnd[1] - 1, bidEnd[2]));
    bidEnd = utils.removeDays(bidEnd, 1).getTime();

    this.state = {
      filterDisplay: false,
      categoryId: this.props.categoryId,
      fetchFromFilter: false,
      sessionStartDate: bidStart,
      sessionEndDate: bidEnd,
    };

    this.sessionData = {
      categoryId: sessionStorage.getItem(CATEGORY_ID),
      bidSessionId: this.props.bidSessionId,
      staffId,
      bidSessionDateRange: { startInclusive: bidStart, endInclusive: bidEnd },
      includeBidAnalysis: false,
    };

    this.notificationShouldAppear = false;
  }
  componentDidMount() {
    const lastViewedBidSessionId = sessionStorage.getItem('lastViewedBidSessionId') || 0;
    const currentBidSessionId = this.props.currentBid.bidSessionId;
    const clearFilter = +lastViewedBidSessionId !== currentBidSessionId;
    const filter = clearFilter ? null : { ...this.props.filter };
    const persistFilter = true;

    /*
      No matter if filter configuration is persisted in browser refresh,
      if user reload the page, firstResult and maxResults must be set to
      its original values.
    */
    if (filter) {
      filter.firstResult = 0;
      filter.maxResults = openTimeSearch.MINIMUM_ITEMS;
    }

    // Save the current bid session as the last viewed session by the user
    sessionStorage.setItem('lastViewedBidSessionId', currentBidSessionId);
    sessionStorage.setItem('islastViewedDefault', false);

    if (this.props.currentBid.bidSessionId === +this.props.bidSessionId) {
      this.props.fetchOpenTimeActivities(this.sessionData, filter, persistFilter);
    }
  }
  UNSAFE_componentWillReceiveProps(nextProps) {
    const { showNotification } = this.props;
    const analysisFromBidAnalizer = nextProps.analysis;
    const openTimeSearchLabels = this.props.labels?.openTimeSearchLabels || {};
    const { ANALYZER } = openTimeSearchLabels;
    const MSG = ANALYZER?.MESSAGES || {};

    if (Object.keys(analysisFromBidAnalizer).length > 0 && this.notificationShouldAppear) {
      if (analysisFromBidAnalizer.result === ANALYZER.NO_DATA) {
        showNotification(`${MSG.PREFIX}: ${analysisFromBidAnalizer.reason}`);
      } else if (analysisFromBidAnalizer.result === ANALYZER.NOT_AVAILABLE) {
        showNotification(`${MSG.NOT_AVAILABLE}`);
      } else if (analysisFromBidAnalizer.error) {
        const { status, data } = analysisFromBidAnalizer.error;

        // This conditional are based on CRSPROD-3220 error response reference
        if (status === httpStatus.NO_CONTENT) {
          showNotification(`${MSG.PREFIX}: ${data.message}`);
        } else if (status === httpStatus.NOT_FOUND) {
          showNotification(`${MSG.NOT_AVAILABLE}`);
        } else {
          // this case right now is for 500 based on CRSPROD-3220
          showNotification(`${MSG.PREFIX}: ${MSG.NOT_RESPONDING}`);
        }
      } else {
        this.sessionData.includeBidAnalysis = true;
      }

      /*
      This notificationShouldAppear flag will prevent that Open Time Search only shows the
      Notification component only after handleRefresh is executed.
      */
      this.notificationShouldAppear = false;
    }
    if (this.props.preferenceHasChange !== nextProps.preferenceHasChange) {
      this.sessionData.includeBidAnalysis = false;
    }
  }

  handlePagination = (maxResults, firstResult) => {
    const filter = { ...this.props.filter };
    const persistFilter = true;
    filter.maxResults = maxResults;
    filter.firstResult = firstResult;
    this.props.fetchOpenTimeActivities(this.sessionData, filter, persistFilter);
  }

  handleRefresh = () => {
    const openTimeSearchLabels = this.props.labels?.openTimeSearchLabels || {};
    const { ANALYZER } = openTimeSearchLabels;
    const MSG = ANALYZER?.MESSAGES || {};

    const {
      fetchBidAnalyzerWithOpenTimeActivities,
      isFetchingBidAnalyzer,
      showNotification,
      bidPreferencesTotalEntries,
    } = this.props;
    const filter = { ...this.props.filter };
    filter.sortCriteria = openTimeSearch.sort.BID_ANALYSIS_RANK;
    filter.sortDirection = openTimeSearch.ASCENDING;
    
    if (isFetchingBidAnalyzer) return; // Disable click action while fetching data

    this.notificationShouldAppear = true;
    const totalEntries = bidPreferencesTotalEntries();

    if (totalEntries && totalEntries > 0) {
      // Update filter state to persist bid analyzer sorting for pagination
      this.props.updateFilterState(filter, true);

      fetchBidAnalyzerWithOpenTimeActivities(
        this.sessionData,
        filter,
      );
    } else {
      showNotification(`${MSG.PREFIX}: ${MSG.NO_BIDS}`);
    }
  }

  handleSort = (criteria, direction) => {
    const filter = { ...this.props.filter };
    direction = direction ? openTimeSearch.ASCENDING : openTimeSearch.DESCENDING;
    filter.sortCriteria = criteria;
    filter.sortDirection = direction;
    this.props.fetchOpenTimeActivities(this.sessionData, filter, true);
  }

  render() {
    const openTimeSearchLabels = this.props.labels?.openTimeSearchLabels || {};
    const calendarLabels = this.props.labels?.calendarLabels || {};
    const commonLabels = this.props.labels?.commonLabels || {};
    let { activities } = this.props;
    const { isFetching, stations } = this.props;
    activities = (Object.keys(activities).length !== 0) ? activities.activities : {};

    const {
      bidPreferencesTotalEntries,
      handleActionPreference,
      showNotification,
      bidSession,
      bidSessionId,
      calendarStartDateStr,
      calendarEndDateStr,
      containerWidth,
      totalDaysInCalendar,
      activePreferenceOptionsIds,
      actionType,
      preassigments
    } = this.props;
    const minContainerWidth = calendar.DAY_MIN_LENGTH * totalDaysInCalendar;
    const container = (containerWidth < minContainerWidth) ? minContainerWidth : containerWidth;

    const totalEntries = activities?.totalEntries ? activities?.totalEntries : 0;
    const activityArray = activities?.entries ? activities?.entries : [];
    const layoverDtoList = activities?.pairingLayoverDtoList ? activities?.pairingLayoverDtoList : [];
    const layoverStations = stations.layover ? stations.layover : {};
    const deadHeadValueMap = layoverStations.deadHeadValueMap ? layoverStations.deadHeadValueMap : {};
    const calendarStartDateTs = new Date(calendarStartDateStr).getTime();
    const calendarEndDateTs = new Date(calendarEndDateStr).getTime();
    const calendarDayWidth = (container / totalDaysInCalendar) - 0.02;
    const calendarHourWidth = calendarDayWidth / 24;
    const stationId = bidSession.user.currentCategory.stationId;

    const renderActivities = activityArray.map(
      (activity, index) => {
        const activityStartDateString = utils.formatActivityDate(utils.getUTC(activity.startDate));
        const activityEndDateString = utils.formatActivityDate(utils.getUTC(activity.endDate));
        let bidAnalysisRank = activity.bidAnalysisRank || openTimeSearchLabels.N_A;
        bidAnalysisRank = bidAnalysisRank === -1 ? 0 : bidAnalysisRank;
        bidAnalysisRank = bidAnalysisRank.toString();

        let activityStartDateTs = activity.startDate;
        let activityEndDateTs = activity.endDate;

        const activityOutOfCalendar = isActivityOutOfPeriod(
          calendarStartDateTs, calendarEndDateTs, activity.startDate, activity.endDate
        );

        const activityOnlyEndsInsideCalendar = between(
          calendarStartDateTs, activity.startDate, activity.endDate
        );

        if (activityOutOfCalendar) {
          /* An activity is completely out of the calendar period */
          activityStartDateTs = calendarStartDateTs;
          activityEndDateTs = calendarStartDateTs;
        } else if (activityOnlyEndsInsideCalendar) {
          /* An activity starts prior the calendar start date, but ends inside the calendar */
          activityStartDateTs = calendarStartDateTs;
        }

        const activityDurationInHours = utils.getHourDiff(
          activityStartDateTs,
          activityEndDateTs
        );
        const hoursFromCalendarStart = utils.getHourDiff(calendarStartDateTs, activityStartDateTs);
        const leftFromCalendarStartPx = calendarHourWidth * hoursFromCalendarStart;
        const activityWidthPx = activityDurationInHours * calendarHourWidth;
        const id = activity.activityId;
        const filteredDtoElement = layoverDtoList.find(layover => layover.activityId === id);
        const filteredLayoverElement = filteredDtoElement ? filteredDtoElement.layoverList : [];

        let layovers = [];
        if (activity.type !== openTimeSearchConfig.activityTypes.TRP) {
          layovers = openTimeSearchLabels.N_A;
        } else {
          layovers = filteredLayoverElement;
          if (layovers.length === 0) {
            layovers = OpenTimeSearchTypes.NO_LAYOVER;
          }
        }
        let deadhead;
        if (deadHeadValueMap[id]) {
          if (`${deadHeadValueMap[id]}` === '< >') {
            deadhead = '<';
          } else {
            deadhead = `${deadHeadValueMap[id]}`;
          }
        }
        const i = index;
        const stationIds = sessionStorage.getItem('admin') === commonLabels.ADMIN ? sessionStorage.getItem(STATION_ID) : stationId;
        let selectedActivity = false
        for(let i = 0; i <= preassigments.length; i++){
          if(preassigments[i]?.activityId === activity.activityId) selectedActivity = true
        }

        return (<OpenTimeSearchItem
          key={`${activity.activityId}${i}`}
          activity={activity}
          startDate={activityStartDateString}
          endDate={activityEndDateString}
          ranking={bidAnalysisRank}
          activityStationId={stationIds}
          bidPreferencesTotalEntries={bidPreferencesTotalEntries}
          handleActionPreference={handleActionPreference}
          showNotification={showNotification}
          bidSession={bidSession}
          bidSessionId={bidSessionId}
          leftPosition={leftFromCalendarStartPx}
          activityWidth={activityWidthPx}
          activePreferenceOptionsIds={activePreferenceOptionsIds}
          layover={`${layovers}`}
          deadhead={deadhead}
          actionType={actionType}
          selectedActivity={selectedActivity}
          fromAddButton={this.props.fromAddButton}
          setFromAddButton={this.props.setFromAddButton}
        />);
      }
    );

    const titleStyle = {
      fontSize: '1rem'
    };
    const filter = { ...this.props.filter };
    return (
      <div style={{ background: common.colors.CI_BG }}>
        <CardHead
          title={openTimeSearchLabels.TITLE}
          headClassName="Open-Search-Head"
          titleStyle={titleStyle}
          subtitle={openTimeSearchLabels.SUBTITLE}
        />
        <FilterBar
          sessionData={this.sessionData}
          actionType={actionType}
          fromAddButton={this.props.fromAddButton}
          setFromAddButton={this.props.setFromAddButton}
        />
        <OpenTimeSearchTable
          handlePagination={this.handlePagination}
          handleRefresh={this.handleRefresh}
          activities={renderActivities}
          totalEntries={totalEntries}
          handleSort={this.handleSort}
          fetchFromFilter={this.state.fetchFromFilter}
          isFetching={isFetching}
          isFetchingBidAnalyzer={this.props.isFetchingBidAnalyzer}
          filter={filter}
          setFromAddButton={this.props.setFromAddButton}
          minContainerWidth={minContainerWidth}
        />
      </div>
    );
  }
}

OpenTimeSearch.propTypes = {
  fetchOpenTimeActivities: PropTypes.func.isRequired,
  fetchBidAnalyzerWithOpenTimeActivities: PropTypes.func.isRequired,
  activities: PropTypes.any.isRequired,
  analysis: PropTypes.any,
  currentBid: PropTypes.any.isRequired,
  categoryId: PropTypes.number.isRequired,
  stationId: PropTypes.string,
  bidPreferencesTotalEntries: PropTypes.func.isRequired,
  handleActionPreference: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
  bidSession: PropTypes.object.isRequired,
  bidSessionId: PropTypes.string.isRequired,
  user: PropTypes.object,
  preferenceHasChange: PropTypes.bool,
  isFetching: PropTypes.bool.isRequired,
  isFetchingBidAnalyzer: PropTypes.bool.isRequired,
  totalDaysInCalendar: PropTypes.number.isRequired,
  calendarStartDateStr: PropTypes.any.isRequired,
  calendarEndDateStr: PropTypes.any.isRequired,
  containerWidth: PropTypes.number.isRequired,
  filter: PropTypes.object.isRequired,
  bidPreferences: PropTypes.object.isRequired,
  activePreferenceOptionsIds: PropTypes.array.isRequired,
  stations: PropTypes.object.isRequired,
  fromAddButton: PropTypes.bool,
  setFromAddButton: PropTypes.func,
};

OpenTimeSearch.defaultProps = {
  currentBid: {},
  categoryId: 0,
  stationId: '',
  activities: [],
  analysis: {},
  user: {},
  preferenceHasChange: false,
  isFetchingBidAnalyzer: false,
  preassigments: [],
  fromAddButton: false,
  setFromAddButton: () => { }
};

const mapStateToProps = state => ({
  ...state.openTimeSearch,
  ...state.openTimeSearch.results,
  ...state.openTimeSearch.bidAnalyzer,
  ...state.login.user.currentCategory,
  ...state.bidSession.bidSession,
  isFetchingBidAnalyzer: state.openTimeSearch.bidAnalyzer.isFetching,
  preassigments: state.linearCalendar.activities?.activities?.entries,
});

const mapDispatchToProps = dispatch => ({
  fetchOpenTimeActivities: (sessionData, filterPayload, persistFilter) => dispatch(
    actions.fetchOpenTimeActivities(sessionData, filterPayload, persistFilter)
  ),
  fetchBidAnalyzerWithOpenTimeActivities: (sessionData, filterPayload) =>
    dispatch(
      actions.fetchBidAnalyzerWithOpenTimeActivities(
        sessionData,
        filterPayload,
      )
    ),
  updateFilterState: (filterPayload, persistFilter) => dispatch(
    actions.fetchOpenTimeActivitiesRequest(filterPayload, persistFilter)
  )
});

const ConnectedOpenTimeSearch = connect(mapStateToProps, mapDispatchToProps)(Dimensions()(OpenTimeSearch));

function OpenTimeSearchWrapper(props) {
  const { labels } = useLanguage();
  return <ConnectedOpenTimeSearch {...props} labels={labels} />;
}

export default OpenTimeSearchWrapper;
